/*
 * Escape hatch so a user can never get permanently stuck in a broken sign-in state (e.g. a redirect
 * loop or a stale bootstrap discovery document caused by a not-yet-fixed bug).
 *
 * Browser: opening the app with "?reset" clears all locally stored data and ends the Keycloak SSO
 * session (RP-initiated logout - Keycloak's own session cookies are HttpOnly and cannot be cleared
 * from JS). It is handled in main.ts before Angular bootstraps, so it still works when the app
 * itself is broken.
 * Desktop: reuses the existing Tauri factory reset (factory_reset_command), which already clears
 * tokens, the webview cache and restarts the app.
 *
 * Deliberately has no Angular dependencies and no static package imports, and logs via console,
 * because main.ts imports it before Native Federation and Angular are initialized.
 */

import { readLogSettings, writeLogSettings } from './log-settings';

export const RESET_QUERY_PARAM = 'reset';

const PREFIX_LOG = '[AppReset]';
const isDesktopEnv = (): boolean => typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;

// angular-oauth2-oidc keeps its tokens in sessionStorage by default (no custom OAuthStorage is provided).
const ID_TOKEN_KEY = 'id_token';
// Locale chosen by the user (see UiStore.setLocale()).
const LANGUAGE_KEY = 'language';
const RESET_CONFIRM_KEY = 'res_ResetSettingsConfirm';
const RESET_CONFIRM_FALLBACK = 'All locally stored data will be cleared. Continue?';

const LOGIN_REDIRECTS_KEY = 'hydra_login_redirects';
// URL the user opened before being sent to the IdP (e.g. a deep link in a new tab), restored by SsoRedirectComponent
const LOGIN_RETURN_URL_KEY = 'hydra_login_return_url';
// A normal sign-in triggers exactly one redirect to the IdP. Several within a short window means
// the app keeps bouncing between itself and the IdP without ever completing the sign-in.
const LOGIN_LOOP_MAX_REDIRECTS = 3;
const LOGIN_LOOP_WINDOW_MS = 30_000;

const OIDC_DISCOVERY_TIMEOUT_MS = 5000;

/**
 * Returns the token's issuer, or null if there is none or it is not an https URL. The token is read
 * unverified from storage, so the issuer is untrusted input that decides where the reset navigates to.
 */
function getIssuer(idToken: string): URL | null {
  try {
    // JWT parts are Base64URL encoded - atob() needs plain Base64.
    const payload = idToken.split('.')[1];
    const claims = JSON.parse(atob(payload.replaceAll('-', '+').replaceAll('_', '/'))) as { iss?: string };
    const issuer = claims.iss ? new URL(claims.iss) : null;
    return issuer?.protocol === 'https:' ? issuer : null;
  } catch {
    return null;
  }
}

/**
 * Builds the RP-initiated logout URL from the stored ID token, so the IdP can clear its own (HttpOnly)
 * session cookies. The end_session_endpoint is read from the issuer's OIDC discovery document (as
 * defined by OpenID Connect Discovery / RP-Initiated Logout) instead of assuming a provider-specific
 * path. Resolves to null when there is no usable token, the issuer cannot be reached in time, or it
 * does not support RP-initiated logout - the reset then only clears local data. Without id_token_hint
 * Keycloak rejects the logout ("Missing parameters: id_token_hint"), so there is no logout without a token.
 */
export async function buildLogoutUrl(idToken: string | null, origin: string): Promise<string | null> {
  const issuer = idToken ? getIssuer(idToken) : null;

  if (!idToken || !issuer) {
    return null;
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), OIDC_DISCOVERY_TIMEOUT_MS);

  try {
    const response = await fetch(`${issuer.href.replace(/\/$/, '')}/.well-known/openid-configuration`, { signal: controller.signal });

    if (!response.ok) {
      return null;
    }

    const { end_session_endpoint: endSessionEndpoint } = (await response.json()) as { end_session_endpoint?: string };

    if (!endSessionEndpoint) {
      return null;
    }

    const url = new URL(endSessionEndpoint);

    // Never send the ID token (or the user) anywhere but the issuer itself.
    if (url.origin !== issuer.origin) {
      console.warn(`${PREFIX_LOG} end_session_endpoint ${url.origin} does not match issuer ${issuer.origin} - only clearing local data`);
      return null;
    }

    url.searchParams.set('id_token_hint', idToken);
    // Same URI the app uses as OAuth redirectUri (and that its regular logout returns to).
    url.searchParams.set('post_logout_redirect_uri', `${origin}/sso`);
    return url.toString();
  } catch (error) {
    console.warn(`${PREFIX_LOG} Could not determine the logout URL - only clearing local data`, error);
    return null;
  } finally {
    clearTimeout(timer);
  }
}

async function clearCacheStorage(): Promise<void> {
  if (typeof caches === 'undefined') {
    return;
  }

  const keys = await caches.keys();
  await Promise.all(keys.map(key => caches.delete(key)));
}

/**
 * Expires all cookies of the app's own origin that are visible to JS. HttpOnly cookies and cookies of
 * other domains (e.g. Keycloak's) cannot be touched from here - Keycloak's are cleared by the logout.
 */
function clearCookies(): void {
  const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';

  document.cookie
    .split(';')
    .map(cookie => cookie.split('=')[0].trim())
    .filter(name => !!name)
    .forEach(name => {
      document.cookie = `${name}=; ${expired}; path=/`;
      document.cookie = `${name}=; ${expired}; path=/; domain=${window.location.hostname}`;
    });
}

/**
 * Clears all browser-side app data (storages, cookies, caches) and ends the Keycloak session (if there is one).
 * Logging is deliberately left untouched, because the logs are needed afterwards to find out what caused
 * the problem the user is resetting for: IndexedDB (holds the logs) is NOT cleared, and the logging
 * settings in localStorage are kept.
 */
export async function performBrowserReset(): Promise<void> {
  console.warn(`${PREFIX_LOG} Resetting app: clearing local data and signing out ...`);

  const idToken = sessionStorage.getItem(ID_TOKEN_KEY) ?? localStorage.getItem(ID_TOKEN_KEY);
  // Resolved before anything is cleared, because it needs the ID token.
  const logoutUrl = await buildLogoutUrl(idToken, window.location.origin);

  const logSettings = readLogSettings();
  localStorage.clear();
  writeLogSettings(logSettings);
  sessionStorage.clear();
  clearCookies();

  try {
    await clearCacheStorage();
  } catch (error) {
    console.warn(`${PREFIX_LOG} Failed to clear cache storage`, error);
  }

  window.location.replace(logoutUrl ?? `${window.location.origin}/`);
}

/**
 * Loads the translated confirmation text without Angular (the app may be broken). Falls back to English.
 */
async function loadResetConfirmText(): Promise<string> {
  const locale = localStorage.getItem(LANGUAGE_KEY) || navigator.language || 'en-US';

  for (const candidate of [locale, 'en-US']) {
    try {
      const response = await fetch(`${window.location.origin}/assets/i18n/${encodeURIComponent(candidate)}.json`);

      if (response.ok) {
        const translations = (await response.json()) as Record<string, string>;

        if (translations[RESET_CONFIRM_KEY]) {
          return translations[RESET_CONFIRM_KEY];
        }
      }
    } catch {
      // Try the next candidate.
    }
  }
  return RESET_CONFIRM_FALLBACK;
}

/**
 * Resolves once the page is no longer prerendering (see handleResetRequest()).
 */
function waitUntilShown(): Promise<void> {
  if (!(document as Document & { prerendering?: boolean }).prerendering) {
    return Promise.resolve();
  }

  console.log(`${PREFIX_LOG} Page is prerendered - waiting until it is shown`);
  return new Promise(resolve => {
    document.addEventListener('prerenderingchange', () => resolve(), { once: true });
  });
}

/**
 * Called from main.ts before bootstrap. The user must always confirm the reset - a native dialog is
 * used because Angular is not running yet. Resolves to true if a reset was started, in which case the
 * app must not be bootstrapped (the page is about to navigate away).
 */
export async function handleResetRequest(): Promise<boolean> {
  const url = new URL(window.location.href);

  if (isDesktopEnv() || !url.searchParams.has(RESET_QUERY_PARAM)) {
    return false;
  }

  // Chrome may prerender a URL while it is typed into the address bar. A prerendered page is hidden and
  // confirm() does not show a dialog there, so the reset would be treated as declined without the user
  // ever seeing it. Ask only once the page is actually shown.
  await waitUntilShown();

  // Drop the parameter in any case, so a reload does not ask again.
  url.searchParams.delete(RESET_QUERY_PARAM);
  window.history.replaceState(window.history.state, '', url.toString());

  // eslint-disable-next-line no-alert -- Angular (and with it PrimeNG's dialog) is not running yet.
  if (!window.confirm(await loadResetConfirmText())) {
    console.log(`${PREFIX_LOG} Reset declined by user`);
    return false;
  }

  performBrowserReset().catch(error => {
    console.error(`${PREFIX_LOG} Reset failed`, error);
    window.location.replace(`${window.location.origin}/`);
  });
  return true;
}

/**
 * Performs the reset. Callers must have asked the user for confirmation already (see AppResetService) -
 * except on desktop, where factory_reset_command shows its own native confirmation dialog.
 */
export async function requestAppReset(): Promise<void> {
  if (isDesktopEnv()) {
    const { invoke } = await import('@tauri-apps/api/core');
    await invoke('factory_reset_command');
    return;
  }

  await performBrowserReset();
}

/**
 * Records a redirect to the IdP login page and returns true if the sign-in is looping, i.e. too many
 * redirects happened within LOGIN_LOOP_WINDOW_MS. On true, the caller must NOT redirect again.
 */
export function registerLoginRedirect(now = Date.now()): boolean {
  let timestamps: number[] = [];

  try {
    const stored = JSON.parse(sessionStorage.getItem(LOGIN_REDIRECTS_KEY) ?? '[]');
    timestamps = Array.isArray(stored) ? stored.filter((t): t is number => typeof t === 'number') : [];
  } catch {
    // Corrupt value - start over.
  }

  timestamps = timestamps.filter(t => now - t < LOGIN_LOOP_WINDOW_MS);

  if (timestamps.length >= LOGIN_LOOP_MAX_REDIRECTS) {
    return true;
  }

  sessionStorage.setItem(LOGIN_REDIRECTS_KEY, JSON.stringify([...timestamps, now]));
  return false;
}

export function clearLoginRedirects(): void {
  sessionStorage.removeItem(LOGIN_REDIRECTS_KEY);
}

// Pages of the login flow itself, never a target to return to after the login
const NON_RETURN_PATHS = new Set(['/', '/sso', '/server-discovery']);
let initialUrl: string | null = null;

const getReturnUrl = ({ pathname, search, hash }: Pick<Location, 'pathname' | 'search' | 'hash'>): string | null =>
  (pathname && !NON_RETURN_PATHS.has(pathname) ? `${pathname}${search}${hash}` : null);

/**
 * Remembers the URL the app was opened with (e.g. a pasted deep link). Must be called on startup, before the
 * router or the discovery flow (e.g. /server-discovery when logged out) navigate away from it.
 */
export function rememberInitialUrl(): void {
  initialUrl = getReturnUrl(window.location);
}

/**
 * Remembers where to go after the login before redirecting to the IdP login page: the current URL, or the URL
 * the app was opened with if the login flow already navigated away from it. The IdP always returns to /sso,
 * so without this a deep link opened while logged out or in a new tab (no tokens in sessionStorage yet) would get lost.
 */
export function saveLoginReturnUrl(): void {
  const url = getReturnUrl(window.location) ?? initialUrl;

  if (!url) {
    sessionStorage.removeItem(LOGIN_RETURN_URL_KEY);
    return;
  }

  sessionStorage.setItem(LOGIN_RETURN_URL_KEY, url);
}

/**
 * Returns (and forgets) the URL saved by saveLoginReturnUrl(). Only app-internal paths are returned.
 */
export function takeLoginReturnUrl(): string | null {
  const url = sessionStorage.getItem(LOGIN_RETURN_URL_KEY);
  sessionStorage.removeItem(LOGIN_RETURN_URL_KEY);
  return url?.startsWith('/') && !url.startsWith('//') ? url : null;
}
